import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../api';
import WorkflowOwnershipFixer from '../components/WorkflowOwnershipFixer';

const WorkflowDebugger: React.FC = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Get user ID from localStorage
  useEffect(() => {
    const userString = localStorage.getItem('user');
    if (userString) {
      try {
        const user = JSON.parse(userString);
        setUserId(user.id);
      } catch (err) {
        console.error('Failed to parse user from localStorage:', err);
      }
    }
  }, []);

  const loadWorkflows = async () => {
    setLoadingWorkflows(true);
    setError(null);
    try {
      const data = await api.getWorkflows();
      setWorkflows(data);
      setSuccess('Successfully loaded workflows');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    } finally {
      setLoadingWorkflows(false);
    }
  };

  const testWorkflowAccess = async (workflowId: string) => {
    try {
      const response = await fetch(`/api/workflows/${workflowId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
        return { success: false, error: data.error || `HTTP ${response.status}` };
      }
      
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  const handleTestAccess = async (workflowId: string) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (!workflow) return;
    
    workflow.testing = true;
    setWorkflows([...workflows]);
    
    const result = await testWorkflowAccess(workflowId);
    
    workflow.testing = false;
    workflow.testResult = result;
    setWorkflows([...workflows]);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Workflow Debugger
      </Typography>
      
      <Typography variant="body1" paragraph>
        This page helps diagnose and fix common workflow issues.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Typography>
                Your User ID: {userId || 'Not found in localStorage'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                This ID should match the userId field on your workflows for proper access.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          <WorkflowOwnershipFixer />
        </Grid>
        
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Workflow Ownership Inspector
                </Typography>
                <Button 
                  variant="contained"
                  onClick={loadWorkflows}
                  disabled={loadingWorkflows}
                  startIcon={loadingWorkflows ? <CircularProgress size={20} /> : null}
                >
                  {loadingWorkflows ? 'Loading...' : 'Load Workflows'}
                </Button>
              </Box>
              
              {workflows.length > 0 ? (
                <List component={Paper} variant="outlined">
                  {workflows.map((workflow, index) => (
                    <React.Fragment key={workflow.id}>
                      {index > 0 && <Divider />}
                      <ListItem>
                        <ListItemText
                          primary={workflow.name}
                          secondary={
                            <>
                              <Typography component="span" variant="body2" display="block">
                                ID: {workflow.id}
                              </Typography>
                              <Typography component="span" variant="body2" display="block" color={workflow.userId === userId ? 'success.main' : 'error.main'}>
                                Owner: {workflow.userId || 'No owner/unknown'} {workflow.userId === userId ? '(You - Access OK)' : '(Not you - Possible access issue)'}
                              </Typography>
                            </>
                          }
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleTestAccess(workflow.id)}
                          disabled={workflow.testing}
                        >
                          {workflow.testing ? <CircularProgress size={24} /> : 'Test Access'}
                        </Button>
                        
                        {workflow.testResult && (
                          <Box ml={2}>
                            {workflow.testResult.success ? (
                              <Alert severity="success" sx={{ px: 1, py: 0 }}>
                                Access OK
                              </Alert>
                            ) : (
                              <Alert severity="error" sx={{ px: 1, py: 0 }}>
                                {workflow.testResult.error}
                              </Alert>
                            )}
                          </Box>
                        )}
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No workflows loaded. Click the button above to load your workflows.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkflowDebugger; 